import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { Questionnaire } from './classes/Questionnaire'
import { EntityType } from './classes/EntityType'
import { Title } from './classes/Title'
import Select from '../../../components/Select'
import DatePicker from '../../../components/DatePicker'
import {
  LISTED_STATE_OPTIONS_WITH_NON_US,
  NON_US_STATE_SELECT_VALUE,
  isListedUsStateOrTerritory,
  toStateSelectValue
} from '../../../utilities/usStateOptions'

export default function QuestionnairePage() {
  const { applicationId } = useParams()
  const navigate = useNavigate()

  const [questionnaire, setQuestionnaire] = useState(new Questionnaire())
  const [entityTypes, setEntityTypes] = useState([])
  const [titles, setTitles] = useState([])
  const [isAccelerate, setIsAcclerate] = useState(false)
  const [isProcessor, setIsProcessor] = useState(false)
  const [processorAcknowledged, setProcessorAcknowledged] = useState(false)
  const [completingQuestionnaire, setCompletingQuestionnaire] = useState(false)

  const isDisabled = completingQuestionnaire ||
    !questionnaire.hasExistingERCClaim2020 ||
    (questionnaire.hasExistingERCClaim2020 === 'Yes' && (!questionnaire.ercAmount2020)) ||
    !questionnaire.hasExistingERCClaim2021 ||
    (questionnaire.hasExistingERCClaim2021 === 'Yes' && (!questionnaire.ercAmount2021)) ||
    !questionnaire.irsFilingType ||
    !questionnaire.estimatedFilingDate ||
    !questionnaire.receivedChecks ||
    (questionnaire.receivedChecks === 'Yes' && (!questionnaire.numOfChecks)) ||
    (!isAccelerate &&
      (
        !questionnaire.existingTaxLiabilities ||
        (questionnaire.existingTaxLiabilities === 'Yes' && (!questionnaire.liabilityAmount))
      )
    ) ||
    !questionnaire.title ||
    !questionnaire.numOfEmployees ||
    !questionnaire.ein ||
    !questionnaire.street ||
    !questionnaire.city ||
    !String(questionnaire.state || '').trim() ||
    !questionnaire.zip ||
    (isProcessor && !processorAcknowledged)

  const handleCompleteQuestionnaire = async () => {
    setCompletingQuestionnaire(true)

    const completeQuestionnaireResponse = await fetch(`${apiURL}/api/applications/${applicationId}/questionnaire/`, {
      method: 'PUT',
      headers: {
        'Authorization': account.accessToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(questionnaire)
    })
    const completeQuestionnaireData = await completeQuestionnaireResponse.json()
    setCompletingQuestionnaire(false)

    if (completeQuestionnaireData.response.error) {
      if (completeQuestionnaireData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleCompleteQuestionnaire() : navigate('/', { state: { expired: true } })
      }
    } else
      navigate(`/applications/${applicationId}/id-verification`)
  }

  useEffect(() => {
    const handleGetQuestionnaire = async () => {
      const questionnaireResponse = await fetch(`${apiURL}/api/applications/${applicationId}/questionnaire/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const questionnaireData = await questionnaireResponse.json()
      if (questionnaireData.response.error) {
        if (questionnaireData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetQuestionnaire() : navigate('/', { state: { expired: true } })
        } else
          console.error(questionnaireData.response.error)
      } else {
        setQuestionnaire(new Questionnaire(questionnaireData.response.questionnaire))
        setIsProcessor(Boolean(questionnaireData.response.isProcessor))
      }
    }

    const handleCheckRefundOption = async () => {
      const refundOptionResponse = await fetch(`${apiURL}/api/applications/${applicationId}/refund-options/selected-option/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const refundOptionData = await refundOptionResponse.json()
      if (refundOptionData.response.error) {
        if (refundOptionData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleCheckRefundOption() : navigate('/', { state: { expired: true } })
        } else
          console.error(refundOptionData.response.error)
      } else
        setIsAcclerate(Boolean(refundOptionData.response.isAccelerate))
    }

    const handleGetEntityTypes = async () => {
      const typesResponse = await fetch(`${apiURL}/api/applications/${applicationId}/questionnaire/entity-types/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const typesData = await typesResponse.json()
      if (typesData.response.error) {
        if (typesData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetEntityTypes() : navigate('/', { state: { expired: true } })
        } else
          console.error(typesData.response.error)
      } else
        setEntityTypes(typesData.response)
    }

    handleGetQuestionnaire()
    handleGetEntityTypes()
  }, [])

  useEffect(() => {
    const handleGetTitles = async () => {
      const typeId = entityTypes.find(t => t.type === questionnaire.irsFilingType)?.typeId ?? ''
      if (!typeId) return

      const titlesResponse = await fetch(`${apiURL}/api/applications/${applicationId}/questionnaire/entity-types/${typeId}/titles/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const titlesData = await titlesResponse.json()
      if (titlesData.response.error) {
        if (titlesData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetTitles() : navigate('/', { state: { expired: true } })
        } else
          console.error(titlesData.response.error)
      } else
        setTitles(titlesData.response)
    }

    if (questionnaire.irsFilingType)
      handleGetTitles()
  }, [questionnaire.irsFilingType])

  return (
    <div className='w-full px-4 sm:px-8 flex flex-col items-center py-8 text-slate-900 dark:text-white transition-colors duration-300'>
      <p className='font-bold text-xl p-4 text-center w-full'>Complete this questionnaire to continue your application.</p>
      <div className='flex flex-col items-center w-full space-y-6'>
        <div className='w-full'>
          <p className='font-bold'>ERC Filing Status</p>
          <div className='w-full flex flex-col items-center gap-10'>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>1. Do you have an existing ERC Claim for 2020?*</label>
              <div className='w-full flex gap-10'>
                {['Yes', 'No'].map(option => (
                  <label key={option} className='flex items-center gap-3 text-gray-800 dark:text-slate-200 cursor-pointer'>
                    <input
                      type='radio'
                      name='hasExistingERCClaim2020'
                      value={option}
                      checked={questionnaire.hasExistingERCClaim2020 === option}
                      onChange={() => setQuestionnaire((prevQuestionnaire) => ({
                        ...prevQuestionnaire,
                        hasExistingERCClaim2020: option
                      }))}
                      className='w-5 h-5 accent-blue-600'
                    />
                    <span className='text-base'>{option}</span>
                  </label>
                ))}
              </div>
              {questionnaire.hasExistingERCClaim2020 === 'Yes' && (
                <div className='mt-6 space-y-5'>
                  <div>
                    <label className='block text-base font-semibold mb-2'>How much ERC did you file for in 2020?*</label>
                    <input
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      value={questionnaire.ercAmount2020}
                      onChange={(e) => {
                        const value = e.target.value
                        if (/^\d*$/.test(value)) {
                          setQuestionnaire((prevQuestionnaire) => ({
                            ...prevQuestionnaire,
                            ercAmount2020: value
                          }))
                        }
                      }}
                      className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                    />
                  </div>
                </div>
              )}
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>2. Do you have an existing ERC Claim for 2021?*</label>
              <div className='w-full flex gap-10'>
                {['Yes', 'No'].map(option => (
                  <label key={option} className='flex items-center gap-3 text-gray-800 dark:text-slate-200 cursor-pointer'>
                    <input
                      type='radio'
                      name='hasExistingERCClaim2021'
                      value={option}
                      checked={questionnaire.hasExistingERCClaim2021 === option}
                      onChange={() => setQuestionnaire((prevQuestionnaire) => ({
                        ...prevQuestionnaire,
                        hasExistingERCClaim2021: option
                      }))}
                      className='w-5 h-5 accent-blue-600'
                    />
                    <span className='text-base'>{option}</span>
                  </label>
                ))}
              </div>
              {questionnaire.hasExistingERCClaim2021 === 'Yes' && (
                <div className='mt-6 space-y-5'>
                  <div>
                    <label className='block text-base font-semibold mb-2'>How much ERC did you file for in 2021?*</label>
                    <input
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      value={questionnaire.ercAmount2021}
                      onChange={(e) => {
                        const value = e.target.value
                        if (/^\d*$/.test(value)) {
                          setQuestionnaire((prevQuestionnaire) => ({
                            ...prevQuestionnaire,
                            ercAmount2021: value
                          }))
                        }
                      }}
                      className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                    />
                  </div>
                </div>
              )}
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>3. Select your IRS Filing Type*</label>
              <Select
                value={questionnaire.irsFilingType}
                onChange={(val) => setQuestionnaire((prevQuestionnaire) => ({
                  ...prevQuestionnaire,
                  irsFilingType: val
                }))}
                options={entityTypes.map((_entityType) => {
                  const entityType = new EntityType(_entityType)
                  return { value: entityType.type, label: entityType.type }
                })}
                placeholder='--- SELECT A TYPE ---'
              />
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>5. When did you most recently file for ERC*</label>
              <DatePicker
                value={questionnaire.estimatedFilingDate}
                onChange={(val) => setQuestionnaire((prevQuestionnaire) => ({
                  ...prevQuestionnaire,
                  estimatedFilingDate: val
                }))}
                placeholder='Select Date'
              />
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>6. Have you received any ERC checks?*</label>
              <div className='w-full flex gap-10'>
                {['Yes', 'No'].map(option => (
                  <label key={option} className='flex items-center gap-3 text-gray-800 dark:text-slate-200 cursor-pointer'>
                    <input
                      type='radio'
                      name='receivedChecks'
                      value={option}
                      checked={questionnaire.receivedChecks === option}
                      onChange={() => setQuestionnaire((prevQuestionnaire) => ({
                        ...prevQuestionnaire,
                        receivedChecks: option
                      }))}
                      className='w-5 h-5 accent-blue-600'
                    />
                    <span className='text-base'>{option}</span>
                  </label>
                ))}
              </div>
              {questionnaire.receivedChecks === 'Yes' && (
                <div className='mt-6 space-y-5'>
                  <div>
                    <label className='block text-base font-semibold mb-2'>How many checks have you received?*</label>
                    <Select
                      value={questionnaire.numOfChecks}
                      onChange={(val) => setQuestionnaire((prevQuestionnaire) => ({
                        ...prevQuestionnaire,
                        numOfChecks: val
                      }))}
                      options={[
                        '1', '2', '3', '4', '5+'
                      ].map(num => ({ value: num, label: num }))}
                      placeholder='--- SELECT A NUMBER ---'
                    />
                  </div>
                </div>
              )}
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>7. What Processing company did you use to file your claim? (Optional)</label>
              <input
                type='text'
                value={questionnaire.processingCompany}
                onChange={(e) => setQuestionnaire((prevQuestionnaire) => ({
                  ...prevQuestionnaire,
                  processingCompany: e.target.value
                }))}
                className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
              />
            </div>
          </div>
        </div>
        {!isAccelerate && (
          <div className='w-full'>
            <p className='font-bold'>Financial & Legal Disclosure</p>
            <div className='w-full flex flex-col items-center gap-10'>
              <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
                <label className='block text-lg font-semibold mb-4 border-b pb-2'>8. Are there any existing unpaid tax liabilities owed by the company to the IRS?</label>
                <div className='flex gap-10'>
                  {['Yes', 'No'].map(option => (
                    <label key={option} className='flex items-center gap-3 text-gray-800 dark:text-slate-200 cursor-pointer'>
                      <input
                        type='radio'
                        name='existingTaxLiabilities'
                        value={option}
                        checked={questionnaire.existingTaxLiabilities === option}
                        onChange={() => setQuestionnaire((prevQuestionnaire) => ({
                          ...prevQuestionnaire,
                          existingTaxLiabilities: option
                        }))}
                        className='w-5 h-5 accent-blue-600'
                      />
                      <span className='text-base'>{option}</span>
                    </label>
                  ))}
                </div>
                {questionnaire.existingTaxLiabilities === 'Yes' && (
                  <div className='mt-6'>
                    <label className='block text-base font-semibold mb-2'>Amount of Liability</label>
                    <input
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      value={questionnaire.liabilityAmount}
                      onChange={(e) => {
                        const value = e.target.value
                        if (/^\d*$/.test(value)) {
                          setQuestionnaire((prevQuestionnaire) => ({
                            ...prevQuestionnaire,
                            liabilityAmount: value
                          }))
                        }
                      }}
                      className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className='w-full'>
          <p className='font-bold'>Business Information</p>
          <div className='w-full flex flex-col items-center gap-10'>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>9. Select your title in the business.</label>
              <Select
                value={questionnaire.title}
                onChange={(val) => setQuestionnaire((prevQuestionnaire) => ({
                  ...prevQuestionnaire,
                  title: val
                }))}
                options={titles.map((_title) => {
                  const title = new Title(_title)
                  return { value: title.title, label: title.title }
                })}
                placeholder='--- SELECT A TITLE ---'
                searchable
              />
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>10. Enter the number of employees you had in 2021</label>
              <input
                type='text'
                value={questionnaire.numOfEmployees}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, '')
                  setQuestionnaire((prevQuestionnaire) => ({
                    ...prevQuestionnaire,
                    numOfEmployees: value
                  }))
                }}
                placeholder='Enter number of employees'
                className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
              />
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>11. Enter your Business EIN</label>
              <input
                type='text'
                value={questionnaire.ein}
                maxLength={10}
                onChange={e => {
                  let value = e.target.value.replace(/\D/g, '')
                  if (value.length > 2)
                    value = value.slice(0, 2) + '-' + value.slice(2, 9)
                  setQuestionnaire((prevQuestionnaire) => ({
                    ...prevQuestionnaire,
                    ein: value
                  }))
                }}
                placeholder='XX-XXXXXXX'
                className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
              />
            </div>
            <div className='w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 transition-colors duration-300'>
              <label className='block text-lg font-semibold mb-4 border-b pb-2'>12. Business Address</label>
              <div className='space-y-4'>
                <input
                  type='text'
                  value={questionnaire.street}
                  onChange={(e) => setQuestionnaire((prevQuestionnaire) => ({
                    ...prevQuestionnaire,
                    street: e.target.value
                  }))}
                  placeholder='Street Address'
                  className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                />
                <div className='flex flex-col md:flex-row gap-4'>
                  <input
                    type='text'
                    value={questionnaire.city}
                    onChange={(e) => setQuestionnaire((prevQuestionnaire) => ({
                      ...prevQuestionnaire,
                      city: e.target.value
                    }))}
                    placeholder='City'
                    className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full md:w-1/2 focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                  />
                  <Select
                    value={toStateSelectValue(questionnaire.state)}
                    onChange={(val) => setQuestionnaire((prevQuestionnaire) => ({
                      ...prevQuestionnaire,
                      state: val === NON_US_STATE_SELECT_VALUE ? '' : val
                    }))}
                    options={LISTED_STATE_OPTIONS_WITH_NON_US}
                    placeholder='--- SELECT A STATE ---'
                    searchable
                    className='w-full md:w-1/4'
                  />
                  <input
                    type='text'
                    value={questionnaire.zip}
                    onChange={e => {
                      const raw = e.target.value
                      const useUsStylePostal = !questionnaire.state || isListedUsStateOrTerritory(questionnaire.state)
                      const value = useUsStylePostal
                        ? raw.replace(/\D/g, '').slice(0, 5)
                        : raw.replace(/[^\w\s-]/g, '').slice(0, 16)
                      setQuestionnaire((prevQuestionnaire) => ({
                        ...prevQuestionnaire,
                        zip: value
                      }))
                    }}
                    maxLength={!questionnaire.state || isListedUsStateOrTerritory(questionnaire.state) ? 5 : 16}
                    placeholder={!questionnaire.state || isListedUsStateOrTerritory(questionnaire.state) ? 'ZIP' : 'ZIP / postal code'}
                    className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full md:w-1/4 focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                  />
                </div>
                {(toStateSelectValue(questionnaire.state) === NON_US_STATE_SELECT_VALUE) && (
                  <div>
                    <label htmlFor='questionnaire-business-state-other' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>
                      State / province / region
                    </label>
                    <input
                      id='questionnaire-business-state-other'
                      type='text'
                      value={questionnaire.state}
                      onChange={(e) => setQuestionnaire((prevQuestionnaire) => ({
                        ...prevQuestionnaire,
                        state: e.target.value
                      }))}
                      placeholder='e.g. Guam, Ontario, England'
                      className='border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 w-full focus:outline-blue-500 dark:bg-slate-700 dark:text-white transition-colors'
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isProcessor && (
        <div className='w-full mt-6'>
          <div className='w-full bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/50 rounded-lg shadow-md p-6 transition-colors duration-300'>
            <label className='flex items-start gap-3 cursor-pointer'>
              <input
                type='checkbox'
                checked={processorAcknowledged}
                onChange={(e) => setProcessorAcknowledged(e.target.checked)}
                className='w-5 h-5 mt-1 accent-blue-600 shrink-0'
              />
              <div className='text-base text-gray-800 dark:text-slate-200'>
                <p className='font-semibold mb-3'>By checking this box, I acknowledge and authorize the following:</p>
                <ul className='list-disc pl-5 space-y-2'>
                  <li>
                    <span className='font-semibold'>Original Processing Fee:</span> I understand and agree that my original ERC claim processing fee was established at the time of my initial engagement and remains in effect for the original claim processing services.
                  </li>
                  <li>
                    <span className='font-semibold'>Acceleration Program Fee:</span> I understand and agree that the fee for the ERC Acceleration Program is separate from my original processing fee and covers additional specialized services to expedite the processing of my claim.
                  </li>
                  <li>
                    <span className='font-semibold'>Authorization to Process Fees:</span> I authorize the company to coordinate, process, and remit all fees associated with both my original ERC claim and the Acceleration Program as outlined in my agreements.
                  </li>
                </ul>
              </div>
            </label>
          </div>
        </div>
      )}
      <button
        onClick={handleCompleteQuestionnaire}
        className='px-10 py-2 bg-ertc-primary rounded-md shadow-md text-white mt-10 disabled:bg-gray-400 disabled:cursor-not-allowed'
        disabled={isDisabled}
      >
        Complete
      </button>
      {completingQuestionnaire && (
        <div className='flex flex-row items-center justify-center gap-2 mt-5 transition-colors duration-300'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
          <p className='text-md text-center text-slate-600 dark:text-slate-300'>Submitting Questionnaire...</p>
        </div>
      )}
    </div>
  )
}
